import type {
    AllowedUrlParams,
    ChangePasswordRequest,
    LoginCredentials,
    RegisterBody
} from "~/api/v1/auth/AuthRepository";
import type { CustomerPortalRequest, CustomerPortalResponse } from "~/api/v2/users/UserRepository";
import type { FreelanceProfile } from "~/stores/userStore";
import { encryptPassword } from "~/help/encrypt";

export interface User {
    id: string;
    company_id: string;
    first_name: string;
    last_name: string;
    phone?: string;
    location?: string;
    email: string;
    selected_role: string;
    signed_picture_url: string;
    profile?: FreelanceProfile;
    subscription_plan?: string;
    is_admin?: boolean;
    is_organization?: boolean;
}

interface RegisterPayload {
    body: RegisterBody;
    params?: AllowedUrlParams;
}

export const authStore = defineStore('auth', {
    state: (): { users: Array<User>, user: User | null, isLoggedIn: boolean } => ({
        user: null as User | null,
        users: [],
        isLoggedIn: false,
    }),
    actions: {
        async register(payload: RegisterPayload) {
            const { $api } = useNuxtApp()
            const response = await $api().v2.auth.createUser(payload.body, payload.params);
            this.setUser(response);
            return response;
        },
        login(payload: LoginCredentials) {
            const { $api } = useNuxtApp()
            return $api().v1.auth.login(payload)
        },
        async me() {
            const { $api } = useNuxtApp()
            const response = await $api().v2.auth.me();
            this.setUser(response);
            return response;
        },
        setUser(newUser: User | null) {
            this.user = newUser;
            this.isLoggedIn = !!newUser;
        },
        clearUser() {
            this.$reset();
        },
        async changePassword(currentPassword: string, newPassword: string) {
            try {
                const { $api } = useNuxtApp();

                // Vérifier que les mots de passe sont valides
                if (!currentPassword || !newPassword) {
                    throw new Error("Les deux mots de passe sont requis");
                }

                // Préparer les données pour l'API
                const data: ChangePasswordRequest = {
                    current_password: currentPassword,
                    new_password: newPassword
                };

                // Appeler l'API
                return await $api().v1.auth.changePassword(data);
            } catch (error) {
                console.error("Erreur lors du changement de mot de passe:", error);
                throw error;
            }
        },
        async deleteAccount(password: string) {
            try {
                const { $api } = useNuxtApp();

                if (!this.user?.user_id) {
                    throw new Error("Impossible de supprimer le compte: ID utilisateur manquant");
                }

                await $api().v1.auth.deleteAccount(this.user.user_id, password);

                this.$patch({
                    user: null,
                    isLoggedIn: false
                });

                navigateTo('/login');

            } catch (error) {
                console.error("Erreur lors de la suppression du compte:", error);
                throw error;
            }
        }
    },
    getters: {
        userHasSubscriptionPlan(state: any) {
            const config = useRuntimeConfig();
            if (config.public.NUXT_PUBLIC_DISABLE_SUBSCRIPTION_GATES === 'true') return 'free';
            return state.user?.subscription_plan;
        },
        isAdmin(state: any) {
            return state.user?.is_admin ?? false;
        },
        isOrganization(state: any) {
            return state.user?.is_organization ?? false;
        }
    }
});
